import { v4 as uuidv4, v5 as uuidv5 } from "uuid"

import { readSessionScoped, removeSessionScoped, writeSessionScoped } from "@/platform/browser/session-store"

export type ConversionSurface = "marketplace" | "core"
type ConversionEventKey = "Search" | "ViewContent" | "AddToCart" | "CompleteRegistration" | "InitiateCheckout" | "AddPaymentInfo" | "Purchase"

const CONVERSION_EVENT_NAMESPACE_V1 = "df1c4950-b73e-4ffb-94d9-9dc917fec4e5"

const ADD_TO_CART_KEY = "nelo_add_to_cart_conversion_id"
const SIGNUP_KEY = "nelo_signup_conversion_id"
const purchaseKey = (idempotencyKey: string) => `nelo_purchase_conversion_id:${idempotencyKey}`
const pendingPurchaseKey = (cartId: string) => `nelo_pending_purchase_conversion:${cartId}`

const readStorage = (key: string): string | undefined => readSessionScoped(key)

const writeStorage = (key: string, value: string): void => writeSessionScoped(key, value)

const clearStorage = (key: string): void => removeSessionScoped(key)

export function createConversionId(): string {
  return uuidv4()
}

export function deriveConversionEventId({
  surface = "marketplace",
  eventKey,
  conversionId,
}: {
  surface?: ConversionSurface
  eventKey: ConversionEventKey
  conversionId: string
}): string {
  return uuidv5(`${surface}:${eventKey}:${conversionId}`, CONVERSION_EVENT_NAMESPACE_V1)
}

export function createAndStoreAddToCartConversionId(): string {
  const id = createConversionId()
  writeStorage(ADD_TO_CART_KEY, id)
  return id
}

export function getStoredAddToCartConversionId(): string | undefined {
  return readStorage(ADD_TO_CART_KEY)
}

export function clearStoredAddToCartConversionId(): void {
  clearStorage(ADD_TO_CART_KEY)
}

export function getSignupConversionId(): string | undefined {
  return readStorage(SIGNUP_KEY)
}

export function ensureSignupConversionId(): string {
  const existing = readStorage(SIGNUP_KEY)
  if (existing) return existing
  const id = createConversionId()
  writeStorage(SIGNUP_KEY, id)
  return id
}

export function getOrCreatePurchaseConversionId(idempotencyKey: string): string {
  const key = purchaseKey(idempotencyKey)
  const existing = readStorage(key)
  if (existing) return existing
  const id = createConversionId()
  writeStorage(key, id)
  return id
}

export function clearStoredPurchaseConversionId(idempotencyKey: string): void {
  clearStorage(purchaseKey(idempotencyKey))
}

interface PendingPurchaseConversion {
  conversionId: string
  transactionId: string
  isFirstLoan?: boolean
}

export function storePendingPurchaseConversionContext({ cartId, conversionId, transactionId, isFirstLoan }: PendingPurchaseConversion & { cartId: string }): void {
  writeStorage(pendingPurchaseKey(cartId), JSON.stringify({ conversionId, transactionId, isFirstLoan }))
}

export function getPendingPurchaseConversionContext(cartId: string): PendingPurchaseConversion | undefined {
  const stored = readStorage(pendingPurchaseKey(cartId))
  if (!stored) return undefined
  try {
    const parsed = JSON.parse(stored) as Partial<PendingPurchaseConversion>
    if (typeof parsed.conversionId === "string" && parsed.conversionId && typeof parsed.transactionId === "string" && parsed.transactionId) {
      return { conversionId: parsed.conversionId, transactionId: parsed.transactionId, ...(typeof parsed.isFirstLoan === "boolean" && { isFirstLoan: parsed.isFirstLoan }) }
    }
  } catch {
    /* empty */
  }
  return undefined
}

export function clearPendingPurchaseConversionContext(cartId: string): void {
  clearStorage(pendingPurchaseKey(cartId))
}
